import React, { useState, useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { saveGenome, deployGenome, publishGenome } from '../api';
import { useNavigate } from 'react-router-dom';
import { GiBoltShield, GiAtom } from 'react-icons/gi';
import { FaCopy, FaPlay, FaRocket, FaGlobe, FaCheckCircle, FaExclamationCircle } from 'react-icons/fa';
import Modal from '../components/Modal';

// --- Styled Components (unchanged) ---
const PageContainer = styled.div`
  max-width: 1000px;
  margin: 0 auto;
`;
const ModeSelector = styled.div`
  display: flex;
  margin-bottom: 2rem;
  border: 1px solid ${props => props.theme.colors.surface2};
  border-radius: 8px;
  overflow: hidden;
`;
const ModeButton = styled.button`
  flex: 1;
  padding: 1.5rem;
  background: ${props => props.$active ? props.theme.colors.primary : 'transparent'};
  color: ${props => props.$active ? 'white' : props.theme.colors.textSecondary};
  border: none;
  font-size: 1.1rem;
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  transition: background-color 0.2s;
  
  &:hover {
    background: ${props => !props.$active && props.theme.colors.surface};
  }
`;
const Form = styled.form`
  background: ${props => props.theme.colors.surface};
  padding: 2rem;
  border-radius: 8px;
  box-shadow: ${props => props.theme.shadow};
`;
const Section = styled.fieldset`
  border: 1px solid ${props => props.theme.colors.surface2};
  border-radius: 5px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
`;
const Legend = styled.legend`
  color: ${props => props.theme.colors.accent};
  font-weight: bold;
  font-size: 1.2rem;
  padding: 0 0.5rem;
`;
const Input = styled.input`
  width: 100%;
  padding: 0.75rem;
  background: ${props => props.theme.colors.background};
  border: 1px solid ${props => props.theme.colors.surface2};
  border-radius: 5px;
  color: ${props => props.theme.colors.text};
  box-sizing: border-box;
  margin-bottom: 1rem;
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
`;
const TextArea = styled.textarea`
  width: 100%;
  min-height: 150px;
  padding: 0.75rem;
  background: ${props => props.theme.colors.background};
  border: 1px solid ${props => props.theme.colors.surface2};
  border-radius: 5px;
  color: ${props => props.theme.colors.text};
  box-sizing: border-box;
  font-family: monospace;
  margin-bottom: 1rem;
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.primary};
  }
`;
const SliderGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  margin-bottom: 1rem;
`;
const SliderRow = styled.div`
  display: grid;
  grid-template-columns: 120px 1fr 50px;
  align-items: center;
  gap: 1rem;
  
  label {
    color: ${props => props.theme.colors.textSecondary};
  }
  
  input[type="range"] {
    flex: 1;
    -webkit-appearance: none;
    appearance: none;
    width: 100%;
    height: 8px;
    background: ${props => props.theme.colors.background};
    border-radius: 5px;
    outline: none;
    
    &::-webkit-slider-thumb {
      -webkit-appearance: none;
      appearance: none;
      width: 20px;
      height: 20px;
      background: ${props => props.theme.colors.primary};
      cursor: pointer;
      border-radius: 50%;
    }
  }
  
  span {
    text-align: right;
    color: ${props => props.theme.colors.accent};
    font-weight: 500;
  }
`;
const CheckboxRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 1rem;
  
  input {
    width: 20px;
    height: 20px;
  }
`;
const Button = styled.button`
  padding: 0.75rem 1.5rem;
  border: none;
  border-radius: 5px;
  background: ${props => props.theme.colors.primary};
  color: white;
  font-size: 1rem;
  font-weight: 500;
  cursor: pointer;
  margin-right: 1rem;
  transition: background-color 0.2s;
  
  &:disabled {
    background: ${props => props.theme.colors.textSecondary};
    cursor: not-allowed;
  }
  &:hover:not(:disabled) {
    opacity: 0.9;
  }
`;
const ErrorMsg = styled.p`
  color: ${props => props.theme.colors.error};
  background: rgba(248, 81, 73, 0.1);
  padding: 0.5rem 1rem;
  border-radius: 5px;
`;
const SuccessMsg = styled.p`
  color: ${props => props.theme.colors.accent};
  background: rgba(86, 227, 159, 0.1);
  padding: 0.5rem 1rem;
  border-radius: 5px;
`;

// --- Deploy Modal Styles (unchanged) ---
const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;
const Spinner = styled.div`
  border: 4px solid ${props => props.theme.colors.surface2};
  border-top: 4px solid ${props => props.theme.colors.primary};
  border-radius: 50%;
  width: 40px;
  height: 40px;
  animation: ${spin} 1s linear infinite;
  margin: 1rem auto;
`;
const DeployStatus = styled.div`
  text-align: center;
  p {
    color: ${props => props.theme.colors.textSecondary};
    font-size: 1.1rem;
  }
  
  svg {
    width: 40px;
    height: 40px;
    
    &.error-icon {
      color: ${props => props.theme.colors.error};
    }
    
    &.success-icon {
      color: ${props => props.theme.colors.accent};
    }
  }
`;
const DeployedURL = styled.p`
  background: ${props => props.theme.colors.background};
  padding: 0.5rem 1rem;
  border-radius: 3px;
  font-family: monospace;
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 1.5rem;
`;
const ModalButtonRow = styled.div`
  display: flex;
  gap: 1rem;
  margin-top: 1.5rem;
`;
const SecondaryButton = styled(Button)`
  background: ${props => props.theme.colors.surface2};
  color: ${props => props.theme.colors.text};
  &:hover:not(:disabled) {
    opacity: 0.9;
    background: ${props => props.theme.colors.surface2};
  }
`;

// --- *** THIS IS THE NEW DEPLOYED CARD (replaces old DeployedSection) *** ---
const DeployedCard = styled.div`
  margin-top: 2rem;
  background: ${props => props.theme.colors.surface};
  border-left: 4px solid ${props => props.theme.colors.accent};
  padding: 1.5rem;
  border-radius: 5px;
  box-shadow: ${props => props.theme.shadow};
  
  h3 {
    color: ${props => props.theme.colors.accent};
    margin-top: 0;
  }
  
  p {
    color: ${props => props.theme.colors.textSecondary};
  }
`;
// --- *** END OF NEW STYLE *** ---


// --- *** MODIFIED DEFAULTS *** ---
const DEFAULT_CONFIG = {
  temperature: 1.0,  // <-- UPDATED
  maxOutputTokens: 2048,
  topK: 40,          // <-- UPDATED
  topP: 1.0,
};

const getInitialFormData = () => ({
  manifest: { name: '', purpose: '' },
  generationConfig: DEFAULT_CONFIG,
  systemPromptTemplate: '',
  context: {
    persona: { role: '', tone: '', style: '' },
    objectives: { goal: '', beliefs: [] },
    guardrails: { rules: [] },
  },
  lineage: { automutate: false },
  fitness: { mutateOn: 5 }
});

const CreatePage = () => {
  const [mode, setMode] = useState('quick');
  const [formData, setFormData] = useState(getInitialFormData());
  
  const [isSaving, setIsSaving] = useState(false);
  const [savedGenome, setSavedGenome] = useState(null); 
  
  const [isDeployModalOpen, setIsDeployModalOpen] = useState(false);
  const [isDeploying, setIsDeploying] = useState(false);
  const [isPublishing, setIsPublishing] = useState(false);
  const [deployedGenome, setDeployedGenome] = useState(null); 
  const [deployError, setDeployError] = useState('');
  const [publishSuccess, setPublishSuccess] = useState('');
  
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const navigate = useNavigate();

  useEffect(() => {
    // This effect is correct. It clears the form when you switch modes.
    setFormData(getInitialFormData());
    setSavedGenome(null);
    setDeployedGenome(null);
    setError('');
    setSuccess('');
  }, [mode]);

  // --- (All handleChange... and isFormValid functions are unchanged) ---
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (name.includes('.')) {
      const parts = name.split('.');
      setFormData(prev => {
        let newForm = JSON.parse(JSON.stringify(prev));
        let current = newForm;
        for (let i = 0; i < parts.length - 1; i++) {
          current = current[parts[i]];
        }
        current[parts[parts.length - 1]] = type === 'checkbox' ? checked : value;
        return newForm;
      });
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };
  const handleConfigChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      generationConfig: {
        ...prev.generationConfig,
        [name]: parseFloat(value)
      }
    }));
  };
  const handleContextArrayChange = (section, field, value) => {
    setFormData(prev => ({
      ...prev,
      context: {
        ...prev.context,
        [section]: {
          ...prev.context[section],
          [field]: value.split('\n')
        }
      }
    }));
  };
  const isFormValid = () => {
    if (!formData.manifest.name || !formData.manifest.purpose) return false;
    if (mode === 'quick' && !formData.systemPromptTemplate) return false;
    if (mode === 'architect' && (!formData.context.persona.role || !formData.context.objectives.goal)) {
        return false;
    }
    return true;
  };
  
  const handleSave = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsSaving(true);
    
    let payload = {
      manifest: formData.manifest,
      config: {
        serviceType: "GEMINI_TEXT",
        modelName: "gemini-2.5-flash",
        generationConfig: formData.generationConfig
      }
    };
    
    if (mode === 'quick') {
      payload.systemPromptTemplate = formData.systemPromptTemplate;
      payload.lineage = { automutate: false };
      payload.context = {};
    } else {
      payload.context = formData.context;
      payload.lineage = { automutate: formData.lineage.automutate };
      if (formData.lineage.automutate) {
        payload.fitness = { mutateOn: parseInt(formData.fitness.mutateOn) };
      }
    }
    
    try {
      const { data } = await saveGenome(payload);
      setSavedGenome(data); 
      setSuccess("Genome saved successfully! Ready to deploy.");
    } catch (err) {
      setError("Failed to save genome. " + err.message);
    } finally {
      setIsSaving(false);
    }
  };
  
  const handleDeploy = async () => {
    if (!savedGenome) return;
    
    setIsDeploying(true);
    setDeployError('');
    setPublishSuccess('');

    try {
      const { data } = await deployGenome(savedGenome.metadata.genomeId);
      setDeployedGenome(data); 
    } catch (err) {
      setDeployError("Failed to deploy genome. This can take 1-3 minutes. " + err.message);
    } finally {
      setIsDeploying(false);
    }
  };

  const handlePublish = async () => {
    if (!deployedGenome) return;
    
    setIsPublishing(true);
    setDeployError('');
    setPublishSuccess('');

    try {
      await publishGenome(deployedGenome.metadata.genomeId);
      setPublishSuccess("Genome published to gallery!");
    } catch (err) {
      setDeployError("Failed to publish genome: " + err.message);
    } finally {
      setIsPublishing(false);
    }
  };

  const openDeployModal = () => {
    setIsDeployModalOpen(true);
    handleDeploy(); 
  };

  // --- *** THIS IS THE MODIFIED FUNCTION *** ---
  const closeDeployModal = () => {
    setIsDeployModalOpen(false);
    // We DON'T reset the form here.
    // The main page will now show the DeployedCard.
  };
  
  // --- *** NEW FUNCTION TO RESET PAGE *** ---
  const createNewGenome = () => {
    setSavedGenome(null);
    setDeployedGenome(null);
    setFormData(getInitialFormData());
    setError('');
    setSuccess('');
  };

  return (
    <PageContainer>
      <ModeSelector>
        <ModeButton $active={mode === 'quick'} onClick={() => setMode('quick')}>
          <GiBoltShield size={30} />
          Quick Create
          <span style={{fontSize: '0.8rem', fontWeight: 300}}>Just write a prompt</span>
        </ModeButton>
        <ModeButton $active={mode === 'architect'} onClick={() => setMode('architect')}>
          <GiAtom size={30} />
          Architect Mode
          <span style={{fontSize: '0.8rem', fontWeight: 300}}>Fine-tune the context</span>
        </ModeButton>
      </ModeSelector>
      
      {/* --- *** MODIFIED RENDER LOGIC *** --- */}
      
      {!deployedGenome ? (
        // STATE 1: Form is visible
        <Form onSubmit={handleSave}>
          <Section>
            <Legend>1. Manifest</Legend>
            <Input name="manifest.name" placeholder="Genome Name" value={formData.manifest.name} onChange={handleChange} />
            <Input name="manifest.purpose" placeholder="Purpose" value={formData.manifest.purpose} onChange={handleChange} />
          </Section>
          
          {mode === 'quick' ? (
            <Section>
              <Legend>2. System Prompt</Legend>
              <TextArea name="systemPromptTemplate" placeholder="You are a helpful assistant..." value={formData.systemPromptTemplate} onChange={handleChange} />
            </Section>
          ) : (
            <Section>
              <Legend>2. Context (Architect Mode)</Legend>
              <Input name="context.objectives.goal" placeholder="Primary Goal (e.g., 'To be a sarcastic assistant')" value={formData.context.objectives.goal} onChange={handleChange} />
              <Input name="context.persona.role" placeholder="Role (e.g., 'an expert summarizer')" value={formData.context.persona.role} onChange={handleChange} />
              <Input name="context.persona.tone" placeholder="Tone (e.g., 'extremely sarcastic')" value={formData.context.persona.tone} onChange={handleChange} />
              <Input name="context.persona.style" placeholder="Style (e.g., 'concise and to-the-point')" value={formData.context.persona.style} onChange={handleChange} />
              <TextArea 
                placeholder="Beliefs (one per line)" 
                value={formData.context.objectives.beliefs.join('\n')} 
                onChange={(e) => handleContextArrayChange('objectives', 'beliefs', e.target.value)} 
              />
              <TextArea 
                placeholder="Rules (one per line)" 
                value={formData.context.guardrails.rules.join('\n')} 
                onChange={(e) => handleContextArrayChange('guardrails', 'rules', e.target.value)} 
              />
            </Section>
          )}
          
          <Section>
            <Legend>3. Configuration</Legend>
            <p>Model: gemini-2.5-flash (Fixed)</p>
            <SliderGroup>
              <SliderRow>
                <label>Temperature</label>
                <input type="range" name="temperature" min="0" max="2" step="0.1" value={formData.generationConfig.temperature} onChange={handleConfigChange} />
                <span>{formData.generationConfig.temperature}</span>
              </SliderRow>
              <SliderRow>
                <label>Top K</label>
                <input type="range" name="topK" min="1" max="64" step="1" value={formData.generationConfig.topK} onChange={handleConfigChange} />
                <span>{formData.generationConfig.topK}</span>
              </SliderRow>
              <SliderRow>
                <label>Max Tokens</label>
                <input type="range" name="maxOutputTokens" min="512" max="8192" step="512" value={formData.generationConfig.maxOutputTokens} onChange={handleConfigChange} />
                <span>{formData.generationConfig.maxOutputTokens}</span>
              </SliderRow>
            </SliderGroup>
          </Section>
          
          {mode === 'architect' && (
            <Section><Legend>4. Evolution</Legend>
              <CheckboxRow>
                <input type="checkbox" id="automutate" name="lineage.automutate" checked={formData.lineage.automutate} onChange={handleChange} />
                <label htmlFor="automutate">Enable Auto-Mutation</label>
              </CheckboxRow>
              {formData.lineage.automutate && (<Input type="number" name="fitness.mutateOn" min="1" value={formData.fitness.mutateOn} onChange={handleChange} />)}
            </Section>
          )}
          
          {error && <ErrorMsg>{error}</ErrorMsg>}
          {success && <SuccessMsg>{success}</SuccessMsg>}
          
          {!savedGenome && (
            <Button type="submit" disabled={!isFormValid() || isSaving}>
              {isSaving ? 'Saving...' : 'Save Genome'}
            </Button>
          )}
          
          {savedGenome && (
            <Button type="button" onClick={openDeployModal} disabled={isDeploying}>
              {isDeploying ? 'Deploying...' : 'Deploy Genome'}
            </Button>
          )}
        </Form>
      ) : (
        // STATE 2: Deployed card is visible
        <DeployedCard>
          <h3><FaRocket /> Genome Deployed!</h3>
          <p>Your agent is live and ready to be tested or published.</p>
          <DeployedURL>
            {deployedGenome.metadata.deploymentUrl}
            <FaCopy style={{cursor: 'pointer'}} onClick={() => navigator.clipboard.writeText(deployedGenome.metadata.deploymentUrl)} />
          </DeployedURL>
          
          {publishSuccess && <SuccessMsg>{publishSuccess}</SuccessMsg>}
          {deployError && <ErrorMsg>{deployError}</ErrorMsg>}

          <ModalButtonRow>
            <Button onClick={() => navigate(`/test/${deployedGenome.metadata.genomeId}`)}>
              <FaPlay /> Test Agent
            </Button>
            <Button onClick={handlePublish} disabled={isPublishing}>
              <FaGlobe /> {isPublishing ? 'Publishing...' : 'Publish to Gallery'}
            </Button>
            <SecondaryButton onClick={createNewGenome}>
              + Create New Genome
            </SecondaryButton>
          </ModalButtonRow>
        </DeployedCard>
      )}
      
      {/* --- END OF MODIFIED RENDER LOGIC --- */}
      
      <Modal 
        title={deployedGenome ? "Genome Deployed!" : "Deploying Genome..."}
        isOpen={isDeployModalOpen}
        onClose={closeDeployModal}
      >
        {isDeploying && (
          <DeployStatus>
            <Spinner />
            <p>Please wait (1-3 minutes)...<br/>Your new agent is being deployed to Cloud Run.</p>
          </DeployStatus>
        )}

        {deployError && (
          <DeployStatus>
            <FaExclamationCircle className="error-icon" />
            <ErrorMsg>{deployError}</ErrorMsg>
          </DeployStatus>
        )}

        {deployedGenome && (
          <DeployStatus>
            <FaCheckCircle className="success-icon" />
            <p>Your agent is live!</p>
            <DeployedURL>
              {deployedGenome.metadata.deploymentUrl}
              <FaCopy style={{cursor: 'pointer'}} onClick={() => navigator.clipboard.writeText(deployedGenome.metadata.deploymentUrl)} />
            </DeployedURL>
            
            {publishSuccess && <SuccessMsg>{publishSuccess}</SuccessMsg>}

            <ModalButtonRow>
              <SecondaryButton onClick={() => {
                navigate(`/test/${deployedGenome.metadata.genomeId}`);
                closeDeployModal();
              }}>
                <FaPlay /> Test Agent
              </SecondaryButton>
              <Button onClick={handlePublish} disabled={isPublishing}>
                <FaGlobe /> {isPublishing ? 'Publishing...' : 'Publish to Gallery'}
              </Button>
            </ModalButtonRow>
          </DeployStatus>
        )}
      </Modal>

    </PageContainer>
  );
};

export default CreatePage;